import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col } from "react-bootstrap";
import "./index.css";
import MassBalanceTable from "./MassBalanceTable";
import MassBalanceSankey from "./MassBalanceSankey";
import { getMassBalance47Data } from "../../../../../apis/Product/MassBalanceFlow47Api";
import { Scale, Table2, GitFork, Info } from "lucide-react";

const MassBalanceContent = ({ productId }) => {
  const { t } = useTranslation();
  const [showSankey, setShowSankey] = useState(false);
  const [massBalanceData, setMassBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    getMassBalance47Data({
      productId,
      setObjects: (data) => {
        setMassBalanceData(data);
        setLoading(false);
      },
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="mass-balance-loading">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!massBalanceData || !massBalanceData.phases || massBalanceData.phases.length === 0) {
    return (
      <div className="mass-balance-empty">
        <div className="text-center">
          <p>{t("NO_MASS_BALANCE_DATA") || "No mass balance data available. Please complete the LCA flows first."}</p>
        </div>
      </div>
    );
  }

  const totalInputs = Number(massBalanceData.total_inputs) || 0;
  const totalOutputs = Number(massBalanceData.total_outputs) || 0;
  const balanceGap = Math.abs(totalInputs - totalOutputs);
  const balanceGapPercent = totalInputs > 0 ? (balanceGap / totalInputs) * 100 : 0;
  const isBalanced = balanceGapPercent <= 2; // 2% tolerance

  return (
    <div className="mass-balance-content-wrapper">
      <div className="mass-balance-header-section">
        <div className="mass-balance-title-section">
          <div className="mass-balance-icon-wrapper">
            <Scale className="mass-balance-icon" />
          </div>
          <div>
            <h3 className="mass-balance-title">{t("MASS_BALANCE") || "Mass Balance"}</h3>
            <p className="mass-balance-subtitle">
              {t("MASS_BALANCE_SUBTITLE") || "Complete material mass balance across all lifecycle phases. Blue = inputs, Red = outputs."}
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="mass-balance-view-toggle">
          <Table2 className="mass-balance-toggle-icon" />
          <label className="mass-balance-toggle-label">
            {t("TABLE") || "Table"}
          </label>
          <div className="mass-balance-switch-wrapper">
            <input
              type="checkbox"
              id="mass-balance-sankey-switch"
              checked={showSankey}
              onChange={(e) => setShowSankey(e.target.checked)}
              className="mass-balance-switch"
            />
            <label htmlFor="mass-balance-sankey-switch" className="mass-balance-switch-label"></label>
          </div>
          <label className="mass-balance-toggle-label">
            {t("SANKEY") || "Sankey"}
          </label>
          <GitFork className="mass-balance-toggle-icon" />
        </div>
      </div>

      {/* Total Mass Balance Box */}
      <div className="mass-balance-total-box">
        <div className="mass-balance-total-header">
          {t("TOTAL_MASS_BALANCE") || "Total Mass Balance"}
        </div>
        <div className="mass-balance-total-row">
          <span className="mass-balance-total-label mass-balance-input-label">
            {t("INPUTS") || "Inputs"}
          </span>
          <span className="mass-balance-total-value mass-balance-input-value">
            {totalInputs.toFixed(2)} kg
          </span>
        </div>
        <div className="mass-balance-total-row">
          <span className="mass-balance-total-label mass-balance-output-label">
            {t("OUTPUTS") || "Outputs"}
          </span>
          <span className="mass-balance-total-value mass-balance-output-value">
            {totalOutputs.toFixed(2)} kg
          </span>
        </div>
        <div className="mass-balance-total-divider"></div>
        <div className="mass-balance-total-row">
          <span className="mass-balance-total-label">
            {t("BALANCE_GAP") || "Δ Balance"}
          </span>
          <span className={`mass-balance-total-value ${isBalanced ? "mass-balance-balanced" : "mass-balance-unbalanced"}`}>
            {balanceGap.toFixed(2)} kg ({balanceGapPercent.toFixed(2)}%) {isBalanced ? "✓" : "⚠"}
          </span>
        </div>
      </div>

      {/* Content */}
      {showSankey ? (
        <div className="mass-balance-sankey-wrapper">
          <MassBalanceSankey productId={productId} />
        </div>
      ) : (
        <div className="mass-balance-table-wrapper">
          <MassBalanceTable data={massBalanceData} />
        </div>
      )}
    </div>
  );
};

export default MassBalanceContent;

